import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import game from '../../../games';

const DEFAULT_HERO_IMAGE = game.heroes.defaultImage;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${props => props.$loading ? 0 : 1};
  transition: opacity 0.2s ease-in-out;
  filter: ${props => props.$grayscale ? 'grayscale(70%) brightness(1.2)' : 'none'};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease-in-out;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 20px;
`;

/**
 * HeroImage Component
 *
 * Uses native lazy loading so only images in/near the viewport are fetched.
 * This prevents mass simultaneous requests when many heroes are rendered at once
 * (e.g. RecordViewer showing 60 drafts).
 */
const HeroImage = ({ hero, size = 'medium', $grayscale = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [useLocal, setUseLocal] = useState(true);

  useEffect(() => {
    setUseLocal(true);
    setLoading(true);
    setError(false);
  }, [hero?.id, hero?.name]);

  if (!hero) return null;

  const filename = hero.imageUrl
    ? hero.imageUrl.substring(hero.imageUrl.lastIndexOf('/') + 1)
    : `${hero.id || hero.name.toLowerCase().replace(/\s/g, '_')}.png`;
  const cdnUrl = hero.imageUrl || `${game.heroes.imageCdn}${filename}`;
  const localUrl = game.heroes.localImagePath
    ? `${process.env.PUBLIC_URL || ''}${game.heroes.localImagePath}${filename}`
    : null;

  const url = localUrl && useLocal ? localUrl : cdnUrl;

  return (
    <ImageContainer>
      <StyledImage
        key={hero.id || hero.name}
        src={url}
        alt={hero.name}
        loading="lazy"
        $loading={loading}
        $grayscale={$grayscale}
        // Inline fallback for the same reason the export connector-path fix
        // needed one: html-to-image's export capture doesn't reliably embed
        // styled-components' dynamic-prop CSS classes onto cloned elements,
        // so opacity/filter driven purely by the $loading/$grayscale class
        // can silently fail to apply in the exported JPEG even though the
        // image is visibly loaded and correct on the live page.
        style={{
          opacity: loading ? 0 : 1,
          filter: $grayscale ? 'grayscale(70%) brightness(1.2)' : 'none',
        }}
        onLoad={() => setLoading(false)}
        onError={() => {
          if (useLocal && localUrl) {
            // Local copy missing — fall back to the CDN once before giving up.
            setUseLocal(false);
            return;
          }
          setError(true);
          setLoading(false);
        }}
      />
      <LoadingOverlay $visible={loading && !error}>
        <LoadingSpinner />
      </LoadingOverlay>
      {error && <ErrorPlaceholder>?</ErrorPlaceholder>}
    </ImageContainer>
  );
};

export default React.memo(HeroImage);
